import { AxiosResponse } from 'axios';
import { useMemo } from 'react';

import api from '../api/api';
import { useAlert } from './use-alert';

type ApiType = typeof api;

type Awaited<T> = T extends PromiseLike<infer U>
  ? { 0: Awaited<U>; 1: U }[U extends PromiseLike<any> ? 0 : 1]
  : T;

type DATA<T extends AxiosResponse<any>> = T extends void ? void : T['data'];

export interface METHOD<F extends (...any) => Promise<any>> {
  (...args: Parameters<F>): Promise<DATA<Awaited<ReturnType<F>>>>;
}

export type DOMAIN<T extends Record<string, (...any) => Promise<AxiosResponse<any> | void>>> = {
  [K in keyof T]:  METHOD<T[K]>;
}

export type API = {
  [K in keyof ApiType]: DOMAIN<ApiType[K]>
}

export const useApi = () => {
  const { error, success } = useAlert();

  return useMemo(() => {
    const getDomain = (d: any, domainName: string) => {
      const _domain = {};
      
      Object.keys(d).forEach((key) => {
        const method = d[key];
        _domain[key] = async (...args: Parameters<typeof method>) => {
          let response = await method(...args);
          if (!response) {return;}
          if (typeof response.data === 'string') { 
            return response.data;
          }
          if (domainName === 'aiAssistant') {
            return response.data;
          }

          if (response.data.code === 401) { // try to refresh token
            try {
              await api.auth.refresh();
            } catch (e) {
              return;
            }
            response = await method(...args);
          }
          if (response.data.code === 401) {
            error(`Ошибка авторизации (${response.data.message})`);

            api.auth.singOut();
            return;
          }
          if (response.status !== 200 && response.status !== 201) {
            error(`Ошибка получения данных (${response.status})`);
            api.auth.singOut();
            return;
          }
          if (response.data.code === 201) {
            success(response.data.message);
            return response.data;
          }
          if (response.data.code !== 200 && key !== 'singIn') {
            error(`Ошибка: ${response.data.message}`);
            return response.data;
          }
          
          return response.data;
        };
      });
      return _domain;
    };
    

    const _api = {};
    
    Object.keys(api).forEach((key) => {
      _api[key] = getDomain(api[key], key);
    });
  
    return _api as API;
  }, [error]);
};